import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"
import type { ChatMessage, NotifyLevel } from "./channels"
import type { MessageReactionTally } from "./message-view"

// Messages per page (first load and each older page). Matches VIP iOS; the
// stream pages back with `before`, so a smaller page only defers older rows.
const MESSAGE_PAGE_SIZE = 50

// Direct-message (1:1 + group DM) data layer. DMs are `conversations` rows with
// scope='conversation' + explicit conversation_members. Unlike channels, the DM
// list is sourced from the purpose-built `list-my-conversations` action (the
// raw CRUD `/conversations` list is firewalled per-tenant and doesn't compute
// participants / unread), then filtered to scope='conversation' here.
//
// CRITICAL — cache keys. A DM's MESSAGE stream reuses the exact channels key
// `["resource", "chat-messages", { conversationId }]` so the vendored realtime
// layer (realtime.ts → tryMessageInsertSplice) splices live chat-messages
// INSERTs into the open DM with zero extra wiring. The DM LIST has its own key
// (it isn't a plain `["resource", ...]` list — it's the action's bundle), so it
// is invalidated on send / create rather than CRUD-patched.

export type ConversationParticipant = {
  // Account id, or the synthetic `guest:<guestId>` for a guest with no account
  // (Event Pass only). Treat it as an opaque key — never resolve it as a user.
  userId: string
  // The participant's guest row (null for staff with no guest row). Optional
  // until every payload carries it.
  guestId?: string | null
  displayName: string
  avatarImageId: string | null
  handle: string | null
  role: string
  /**
   * True when this participant is no longer an active member of the org/event
   * (deactivated or removed). Optional + defaults to active: until the backend
   * projects it, the value is undefined and the UI behaves exactly as before
   * (no badge, compose enabled). See the deactivation helpers below.
   */
  deactivated?: boolean
}

export type ConversationLastMessage = {
  id: string
  authorName: string
  authorRole: string | null
  body: string | null
  kind: string
  createdAt: string
}

// One inbox row as `list-my-conversations` / `create-conversation` emit it.
export type ConversationItem = {
  id: string
  eventId: string
  scope: "channel" | "conversation"
  name: string | null
  topic: string | null
  emoji: string | null
  agendaItemId: string | null
  agendaGroupId: string | null
  attendeeGroupId?: string | null
  includesAllConfirmedGuests: boolean
  pinnedAtTop: boolean
  archivedAt: string | null
  postingMode: string
  // Absent ⇒ "all". See NotifyLevel in ./channels.
  defaultNotifyLevel?: NotifyLevel | null
  canPost: boolean
  participants: ConversationParticipant[]
  lastMessage: ConversationLastMessage | null
  // Real unread count (THE unread rule — server-computed vs my pointer).
  unread: number
  mentionsMe?: boolean
  // My read pointer — snapshot on open for the "New" divider.
  myRead?: { lastReadAt: string; lastReadMessageId: string | null } | null
}

// One row from the DM recipient picker (`list-roster` with forDm=true).
export type RosterPerson = {
  userId: string
  handle: string | null
  displayName: string
  avatarImageId: string | null
  canDm: boolean
}

type ConversationsEnvelope = { success: true; conversations: ConversationItem[] }
export type MessagesEnvelope = {
  success: true
  data: ChatMessage[]
  nextCursor: string | null
  hasMore: boolean
  // Grouped reactions for the returned messages, keyed by message id — bundled
  // by list-conversation-messages so the client seeds useReactions in one pass.
  reactions?: Record<string, MessageReactionTally[]>
}
type RosterEnvelope = { success: true; roster: RosterPerson[] }

// ── query keys ────────────────────────────────────────────────────────────────
// The DM list is per-event; keyed by event so a workspace switch refetches.
export const conversationsKey = (eventId: string) =>
  ["dm-conversations", eventId] as const
// Reuses the channels message-stream key so realtime splices flow through.
export const conversationMessagesKey = (conversationId: string) =>
  ["resource", "chat-messages", { conversationId }] as const
export const dmRosterKey = (eventId: string) => ["dm-roster", eventId] as const

// ── query options ────────────────────────────────────────────────────────────
export function conversationsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: conversationsKey(eventId),
    queryFn: () =>
      api<ConversationsEnvelope>(
        `/api/v1/event/${eventId}/chat/list-my-conversations`,
        { method: "POST", body: {} },
      ),
    // Only DMs / group DMs — channels have their own surface.
    select: (r) => r.conversations.filter((c) => c.scope === "conversation"),
  })
}

export function conversationMessagesQueryOptions(
  eventId: string,
  conversationId: string,
) {
  return queryOptions({
    queryKey: conversationMessagesKey(conversationId),
    queryFn: () =>
      api<MessagesEnvelope>(
        `/api/v1/event/${eventId}/chat/list-conversation-messages`,
        { method: "POST", body: { conversationId, limit: MESSAGE_PAGE_SIZE } },
      ),
    // Shape to match the channels stream envelope ({ data }) so the shared
    // MessageStream + realtime splice (which read `.data`) work unchanged.
    select: (r): MessagesEnvelope => r,
  })
}

export function fetchConversationMessagesPage(
  eventId: string,
  conversationId: string,
  before: string | null,
  limit = MESSAGE_PAGE_SIZE,
) {
  return api<MessagesEnvelope>(
    `/api/v1/event/${eventId}/chat/list-conversation-messages`,
    {
      method: "POST",
      body: {
        conversationId,
        limit,
        ...(before ? { before } : {}),
      },
    },
  )
}

export function dmRosterQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: dmRosterKey(eventId),
    queryFn: () =>
      api<RosterEnvelope>(`/api/v1/event/${eventId}/chat/list-roster`, {
        method: "POST",
        body: { forDm: true },
      }),
    select: (r) => r.roster,
  })
}

// ── mutations ────────────────────────────────────────────────────────────────
export type CreateConversationResult = {
  success: true
  committed: boolean
  deduped?: boolean
  conversation: ConversationItem
}

// Open (or, for an existing 1:1, return) a DM with one or more recipients. The
// backend dedups 1:1 threads and enforces opt-out / block gates (staff bypass).
export async function createConversation(input: {
  eventId: string
  recipientUserIds: string[]
}): Promise<CreateConversationResult> {
  return api<CreateConversationResult>(
    `/api/v1/event/${input.eventId}/chat/create-conversation`,
    {
      method: "POST",
      body: { recipientUserIds: input.recipientUserIds, dryRun: false },
    },
  )
}

// Mark a DM read — idempotent server upsert of the read pointer. Shares the
// same action channels use, so this is best-effort fire-and-forget.
export async function markConversationRead(
  eventId: string,
  conversationId: string,
  lastReadMessageId?: string | null,
): Promise<void> {
  await api(`/api/v1/event/${eventId}/chat/mark-conversation-read`, {
    method: "POST",
    body: { conversationId, ...(lastReadMessageId ? { lastReadMessageId } : {}) },
  })
}

// Drop the unread badge of one conversation in the (action-sourced) list cache
// without a refetch — used on open. Returns true when a row was patched.
export function clearConversationUnread(
  qc: QueryClient,
  eventId: string,
  conversationId: string,
): boolean {
  let patched = false
  qc.setQueryData<ConversationsEnvelope>(conversationsKey(eventId), (old) => {
    if (!old) return old
    const next = old.conversations.map((c) => {
      if (c.id !== conversationId || c.unread === 0) return c
      patched = true
      return { ...c, unread: 0 }
    })
    return patched ? { ...old, conversations: next } : old
  })
  return patched
}

// ── display helpers ───────────────────────────────────────────────────────────

// A DM title is the comma-joined OTHER participants (the signed-in user is
// dropped). A solo thread (note-to-self) falls back to "(you)".
export function dmTitle(
  c: ConversationItem,
  currentUserId: string | null,
): string {
  // "Priya Nair" · "Priya Nair & Liam Walsh" · "Priya, Liam & 2 others".
  // A named group DM keeps its name (matches the apps).
  // Display only — search should match every participant's displayName.
  if (c.name?.trim()) return c.name
  const names = otherParticipants(c, currentUserId).map((p) => p.displayName)
  if (names.length === 0) return "You"
  if (names.length <= 2) return names.join(" & ")
  const first = (n: string) => n.trim().split(/\s+/)[0]
  const rest = names.length - 2
  return `${first(names[0])}, ${first(names[1])} & ${rest} ${rest === 1 ? "other" : "others"}`
}

// "Me" is matched by account id: the console caller is always signed-in staff,
// and staff never hold a synthetic `guest:<id>` userId, so pass guests are
// always "others" (titles fall back to their displayName).
export function otherParticipants(
  c: ConversationItem,
  currentUserId: string | null,
): ConversationParticipant[] {
  const others = c.participants.filter((p) => p.userId !== currentUserId)
  return others.length > 0 ? others : c.participants
}

export function isGroupDm(
  c: ConversationItem,
  currentUserId: string | null,
): boolean {
  return otherParticipants(c, currentUserId).length > 1
}

// The OTHER participants that are no longer active org members. Never includes
// the signed-in user. Empty until the backend projects `deactivated`.
export function deactivatedParticipants(
  c: ConversationItem,
  currentUserId: string | null,
): ConversationParticipant[] {
  return otherParticipants(c, currentUserId).filter((p) => p.deactivated === true)
}

// True only for a 1:1 DM whose sole other participant is deactivated — the case
// where we disable the composer (in a group, others may still be active).
export function isDmRecipientDeactivated(
  c: ConversationItem,
  currentUserId: string | null,
): boolean {
  const others = otherParticipants(c, currentUserId)
  return others.length === 1 && others[0]!.deactivated === true
}

// Deterministic avatar gradient per user so the same person reads the same
// across the rail + thread (mirrors the mockup's coloured initials chips).
const AVATAR_GRADIENTS = [
  "from-sky-400 to-indigo-500",
  "from-amber-300 to-orange-500",
  "from-emerald-400 to-teal-600",
  "from-pink-400 to-rose-600",
  "from-violet-400 to-fuchsia-600",
  "from-teal-400 to-cyan-600",
  "from-rose-400 to-red-500",
  "from-lime-400 to-green-600",
] as const

export function avatarGradient(userId: string): string {
  let h = 0
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]
}

export function personInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  )
}

// Flatten a last-message preview for the rail. Strips whitespace; falls back to
// a kind-aware label for non-text messages (image-only, system).
export function previewText(last: ConversationLastMessage | null): string {
  if (!last) return "No messages yet"
  const body = (last.body ?? "").replace(/\s+/g, " ").trim()
  if (body) return body
  if (last.kind === "image" || last.kind === "attachment") return "Sent an attachment"
  if (last.kind === "system") return body || "—"
  return "Sent a message"
}
